import React, { useState, useMemo } from 'react';
import { Button, Card, FormInput } from '@/components/UI';
import { columnGroups, columnPresets, type ColumnConfig } from '../backtestColumns';

interface ColumnSelectorProps {
    visibleColumns: Set<string>;
    onVisibilityChange: (columns: Set<string>) => void;
    columnRegistry: ColumnConfig[];
    onClose: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({ visibleColumns, onVisibilityChange, columnRegistry, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(columnGroups.map((g) => g.id)));

    // Filter columns based on search
    const filteredGroups = useMemo(() => {
        if (!searchTerm.trim()) return columnGroups;

        return columnGroups
            .map((group) => ({
                ...group,
                columns: group.columns.filter(
                    (col) =>
                        col.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        col.id.toLowerCase().includes(searchTerm.toLowerCase())
                )
            }))
            .filter((group) => group.columns.length > 0);
    }, [searchTerm]);

    const handleColumnToggle = (columnId: string) => {
        const newColumns = new Set(visibleColumns);

        if (newColumns.has(columnId)) {
            newColumns.delete(columnId);
        } else {
            newColumns.add(columnId);
        }

        onVisibilityChange(newColumns);
    };

    const handleGroupToggle = (groupId: string) => {
        const newExpanded = new Set(expandedGroups);

        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }

        setExpandedGroups(newExpanded);
    };

    const handleGroupSelect = (groupColumns: ColumnConfig[], select: boolean) => {
        const newColumns = new Set(visibleColumns);

        groupColumns.forEach((col) => {
            if (select) {
                newColumns.add(col.id);
            } else {
                newColumns.delete(col.id);
            }
        });

        onVisibilityChange(newColumns);
    };

    const handlePreset = (preset: 'basic' | 'performance' | 'full') => {
        onVisibilityChange(new Set(columnPresets[preset]));
    };

    const handleReset = () => {
        const defaultColumns = columnRegistry.filter((col) => col.defaultVisible).map((col) => col.id);
        onVisibilityChange(new Set(defaultColumns));
    };

    return (
        <Card className="w-96 max-h-[70vh] overflow-y-auto flex flex-col">
            <div className="p-4 border-b border-zinc-700">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Column Settings</h3>
                    <button className="text-zinc-400 hover:text-zinc-200 transition-colors" onClick={onClose}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                        </svg>
                    </button>
                </div>

                <div className="flex gap-2 mb-4">
                    <Button size="sm" variant="secondary" onClick={() => handlePreset('basic')}>
                        Basic
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => handlePreset('performance')}>
                        Performance
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => handlePreset('full')}>
                        Full
                    </Button>
                    <Button size="sm" variant="secondary" onClick={handleReset}>
                        Reset
                    </Button>
                </div>

                <FormInput
                    className="w-full"
                    placeholder="Search columns..."
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                {filteredGroups.map((group) => {
                    const isExpanded = expandedGroups.has(group.id);
                    const visibleCount = group.columns.filter((col) => visibleColumns.has(col.id)).length;

                    return (
                        <div className="mb-4" key={group.id}>
                            <div className="flex items-center justify-between mb-2">
                                <button
                                    className="flex items-center gap-2 text-sm font-medium text-zinc-300 hover:text-zinc-100 transition-colors"
                                    onClick={() => handleGroupToggle(group.id)}
                                >
                                    <svg
                                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                                    </svg>
                                    {group.label} ({visibleCount}/{group.columns.length})
                                </button>
                                <div className="flex gap-1">
                                    <button
                                        className="text-xs text-blue-400 hover:text-blue-300"
                                        onClick={() => handleGroupSelect(group.columns, true)}
                                    >
                                        All
                                    </button>
                                    <span className="text-xs text-zinc-500">|</span>
                                    <button
                                        className="text-xs text-blue-400 hover:text-blue-300"
                                        onClick={() => handleGroupSelect(group.columns, false)}
                                    >
                                        None
                                    </button>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="ml-6 space-y-1">
                                    {group.columns.map((column) => (
                                        <label
                                            className="flex items-center gap-2 py-1 cursor-pointer hover:bg-zinc-800 px-2 -mx-2 rounded"
                                            key={column.id}
                                        >
                                            <input
                                                checked={visibleColumns.has(column.id)}
                                                className="w-4 h-4 text-blue-600 bg-zinc-700 border-zinc-600 rounded focus:ring-blue-500 focus:ring-2"
                                                type="checkbox"
                                                onChange={() => handleColumnToggle(column.id)}
                                            />
                                            <span className="text-sm text-zinc-300">{column.label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="p-4 border-t border-zinc-700">
                <div className="text-sm text-zinc-400">{visibleColumns.size} columns selected</div>
            </div>
        </Card>
    );
};

export default ColumnSelector;
